import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, CheckCircle, Clock, DollarSign, TrendingUp, Building2, AlertTriangle, XCircle, Funnel } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { dashboardApi, unidadesApi, prestacoesApi, documentosApi } from '../api/backend';
import { useAuthStore } from '../store/authStore';
import type { DashboardResumo, DocumentoFiscal, DocumentoFiscalStatus, PrestacaoContas, Unidade } from '../types';
import { DashboardPieCard } from '../components/DashboardPieCard'
import { formatCurrencyWithSymbol } from '../helpers/format-money';
import { formatDate } from '../helpers/format-date';
import { getStatusConfig } from '../helpers/get-status-config';

interface DocumentoIndeferido {
  id: number;
  prestacao_id: number;
  nome_fantasia: string;
  numero_documento: string;
  valor_original: number;
  unidade_nome: string;
  mes: number;
  ano: number;
}

export function DashboardPage() {
  const navigate = useNavigate();
  const [resumo, setResumo] = useState<DashboardResumo | null>(null);
  const [loading, setLoading] = useState(true);
  const [totalUnidades, setTotalUnidades] = useState(0);
  const [unidades, setUnidades] = useState<Unidade[]>([])
  const [documentosIndeferidos, setDocumentosIndeferidos] = useState<DocumentoIndeferido[]>([]);
  //const { selectedUnidadeId, isAdmin, setSelectedUnidade } = useAuthStore();
  const [selectedUnidadeId, setSelectedUnidade] = useState<number | null>(null);
  const [documentos, setDocumentos] = useState<DocumentoFiscal[]>([])
  const [prestacoes, setPrestacoes] = useState<PrestacaoContas[]>([])
  const isAdmin = () => true

  useEffect(() => {
    loadResumo();
    loadDocumentos()
  }, [selectedUnidadeId]);

  const loadResumo = async () => {
    try {
      const { user, userType } = useAuthStore.getState();
      
      // Se logado como unidade, filtrar pelos dados da unidade
      let unidadeParaFiltrar: number | undefined = undefined;
      
      if (userType === 'unidade' && user?.unidade_id) {
        unidadeParaFiltrar = user.unidade_id;
      } else if (selectedUnidadeId !== null && selectedUnidadeId !== undefined) {
        // Admin visualizando como unidade
        unidadeParaFiltrar = selectedUnidadeId;
      }

      const [resumoData, prestacoesData] = await Promise.all([
        dashboardApi.resumo(unidadeParaFiltrar),
        prestacoesApi.list(null, selectedUnidadeId)
      ]);

      setResumo(resumoData);
      setPrestacoes(prestacoesData)
      
      // Carregar total de unidades apenas para admin
      //if (isAdmin()) {
        const _unidades = await unidadesApi.list();
        setUnidades(_unidades)
        setTotalUnidades(_unidades.length);
        
        // Buscar documentos indeferidos recentes
        await loadDocumentosIndeferidos();
      //}
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentosIndeferidos = async () => {
    try {
      // Buscar prestações do ano atual
      const anoAtual = new Date().getFullYear();
      const prestacoes = await prestacoesApi.list(anoAtual);
      
      // Para cada prestação, buscar documentos indeferidos
      const indeferidos: DocumentoIndeferido[] = [];
      for (const prestacao of prestacoes) {
        const documentos = await prestacoesApi.getDocumentos(prestacao.id);
        const docsIndeferidos = documentos.filter((doc: any) => doc.validacao_status === 'indeferido');
        
        docsIndeferidos.forEach((doc: any) => {
          indeferidos.push({
            id: doc.id,
            prestacao_id: prestacao.id,
            nome_fantasia: doc.nome_fantasia,
            numero_documento: doc.numero_documento,
            valor_original: doc.valor_original,
            unidade_nome: prestacao.unidade.nome,
            mes: prestacao.mes,
            ano: prestacao.ano,
          });
        });
      }
      
      // Ordenar por mais recente (últimos 5)
      setDocumentosIndeferidos(indeferidos.slice(0, 5));
    } catch (error) {
      console.error('Error loading documentos indeferidos:', error);
    }
  };

  const loadDocumentos = async () => {
    try {
      setLoading(true);

      const documentosData = await documentosApi.search({
        ...(selectedUnidadeId ? { unidadeId: selectedUnidadeId } : {}),
        ordem: "criacao_desc"
        // lancadoDepoisDe: sub(new Date(), { days: 10 })        
      })
    
      setDocumentos(documentosData)
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleChangeUnidade = (unidadeId: number) => {
    setSelectedUnidade(unidadeId)
  }

  const aprovacaoDocsData = useMemo(() => {
    return [
      {
        name: 'Total de Documentos',
        value: prestacoes.reduce((acc, conta) => acc + conta.total_documentos, 0),
        color: '#3b82f6'
      },
      {
        name: 'Aprovados',
        value: prestacoes.reduce((acc, conta) => acc + conta.documentos_deferidos, 0),
        color: '#10b981'
      },
    ]
  }, [prestacoes])
  
  const statusPrestacoesData = useMemo(() => {
    return [
      {
        name: 'Pendentes',
        value: prestacoes.reduce((acc, conta) => acc + conta.documentos_pendentes, 0),
        color: '#3b82f6'
      },
      {
        name: 'Deferidos',
        value: prestacoes.reduce((acc, conta) => acc + conta.documentos_deferidos, 0),
        color: '#10b981'
      },
      {
        name: 'Indeferidos',
        value: prestacoes.reduce((acc, conta) => acc + conta.documentos_indeferidos, 0),
        color: '#ef4444'
      }
    ]
  }, [documentos])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  const baseCards = [
    {
      title: 'Total de Prestações',
      value: resumo?.total_prestacoes || 0,
      valueLabel: 'Qtd. de prestações',
      secondaryLabel: 'Valor total',
      secondaryValue: formatCurrencyWithSymbol(resumo?.valor_total_somado || 0),
      icon: FileText,
      color: 'from-blue-500 to-blue-600',
      textColor: 'text-blue-600 dark:text-blue-400',
    },
    {
      title: 'Documentos Totais',
      value: resumo?.total_documentos || 0,
      icon: CheckCircle,      
      color: 'from-purple-500 to-purple-600',
      textColor: 'text-purple-600 dark:text-purple-400',
    },    
  ];

  const documentCards = [    
    {
      title: 'Pendentes de Aprovação',
      value: resumo?.documentos_pendentes || 0,
      valueLabel: 'Qtd. de documentos',
      secondaryLabel: 'Valor total',
      secondaryValue: formatCurrencyWithSymbol(resumo?.valor_total_pendente || 0),
      icon: Clock,
      color: 'from-yellow-500 to-yellow-600',
      textColor: 'text-yellow-600 dark:text-yellow-400',
    },
    {
      title: 'Documentos Aprovados',
      value: resumo?.documentos_aprovados || 0,
      valueLabel: 'Qtd. de documentos',
      secondaryLabel: 'Valor total',
      secondaryValue: formatCurrencyWithSymbol(resumo?.valor_total_aprovado || 0),
      icon: DollarSign,
      color: 'from-green-500 to-green-600',
      textColor: 'text-green-600 dark:text-green-400',
      
    },
    {
      title: 'Documentos Indeferidos',
      value: resumo?.documentos_indeferidos || 0,
      valueLabel: 'Qtd. de documentos',
      secondaryLabel: 'Valor total',
      secondaryValue: formatCurrencyWithSymbol(resumo?.valor_total_indeferido || 0),
      icon: XCircle,
      color: 'from-red-500 to-red-600',
      textColor: 'text-red-600 dark:text-red-400',
    },
  ];

  // Adicionar card de unidades apenas para admin
  const cards = isAdmin() && selectedUnidadeId === null
    ? [
        ...baseCards,
        {
          title: 'Unidades Cadastradas',
          value: totalUnidades,
          icon: Building2,
          color: 'from-indigo-500 to-indigo-600',
          textColor: 'text-indigo-600 dark:text-indigo-400',
        },
        ...documentCards
      ]
    : [...baseCards, ...documentCards];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Dashboard
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Visão geral das prestações de contas {new Date().getFullYear()}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Funnel className="w-5 h-5" />
              <select onChange={(e) => handleChangeUnidade(parseInt(e.target.value))} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500">
                <option value="">Todas as Unidades</option>
                {unidades.map((unidade) => {
                  return (
                    <option value={unidade.id} selected={selectedUnidadeId == unidade.id}>{unidade.nome}</option>
                  )
                })}
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {cards.map((card, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg bg-gradient-to-br ${card.color}`}>
                  <card.icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                {card.title}
              </h3>
              <div className="space-y-2">
                <div>
                  <p className={`text-2xl font-bold ${card.textColor}`}>
                    {typeof card.value === 'number'
                      ? card.value.toLocaleString('pt-BR')
                      : card.value}
                  </p>
                  {card.valueLabel && (
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {card.valueLabel}
                    </p>
                  )}
                </div>
                {card.secondaryValue && (
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {card.secondaryLabel || 'Valor'}:{' '}
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {card.secondaryValue}
                    </span>
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Notificações de Documentos Indeferidos - Apenas para Admin */}
        {documentosIndeferidos.length > 0 && (
          <div className="mb-6">
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                <h2 className="text-xl font-bold text-red-800 dark:text-red-200">
                  Documentos Indeferidos Recentes
                </h2>
              </div>
              <p className="text-sm text-red-700 dark:text-red-300 mb-4">
                {documentosIndeferidos.length} documento{documentosIndeferidos.length > 1 ? 's' : ''} indeferido{documentosIndeferidos.length > 1 ? 's' : ''} que requer{documentosIndeferidos.length > 1 ? 'm' : ''} atenção
              </p>
              
              <div className="space-y-3">
                {documentosIndeferidos.map((doc) => (
                  <div
                    key={doc.id}
                    onClick={() => navigate(`/prestacoes/${doc.prestacao_id}`)}
                    className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-red-200 dark:border-red-800 hover:border-red-400 dark:hover:border-red-600 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {doc.nome_fantasia}
                          </h3>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                          <p>Unidade: <span className="font-medium">{doc.unidade_nome}</span></p>
                          <p>Período: <span className="font-medium">{doc.mes}/{doc.ano}</span></p>
                          <p>NF: <span className="font-medium">{doc.numero_documento}</span></p>
                          <p>Valor: <span className="font-medium text-red-600 dark:text-red-400">
                            R$ {doc.valor_original.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span></p>
                        </div>
                      </div>
                      <div className="text-xs text-red-600 dark:text-red-400 font-medium bg-red-100 dark:bg-red-900/40 px-3 py-1 rounded-full">
                        INDEFERIDO
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 text-sm text-red-700 dark:text-red-300 italic">
                Clique em uma notificação para abrir a prestação e ver os detalhes
              </div>
            </div>
          </div>
        )}

        {/* TABELA DE DOCUMENTOS */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className='flex items-center justify-between mb-4'>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Últimos Documentos Incluídos
            </h2>
            <Link to="/documentos" className='text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1'>
              <span >Ver todos</span><span>→</span>
            </Link>
          </div>
          <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="min-w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/40">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Número
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Fornecedor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Rubrica
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Valor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Data
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {documentos.length === 0 && (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-6 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                        >
                          Nenhum documento encontrado para os filtros selecionados.
                        </td>
                      </tr>
                    )}

                    {documentos.map((doc) => {
                      const statusCfg = getStatusConfig(doc.validacao_status as DocumentoFiscalStatus);
                      return (
                        <tr key={doc.id}>
                          <td className="px-6 py-4 max-w-32 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                            <Link to={`/prestacoes/${doc.prestacao_id}#documento_${doc.id}`}>
                              {doc.numero_documento || '—'}
                            </Link>
                          </td>
                          <td className="px-6 py-4 max-w-32 text-sm text-gray-900 dark:text-gray-100">
                            {doc.fornecedor_nome || '—'}
                          </td>
                          <td className="px-6 py-4 max-w-32 text-sm">
                            <button
                              type="button"
                              className="text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200 font-medium"
                            >
                              {doc.rubrica_documento || '—'}
                            </button>
                          </td>
                          <td className="px-6 py-4 max-w-32 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                            <strong>{formatCurrencyWithSymbol(doc.valor_original)}</strong>
                          </td>
                          <td className="px-6 py-4 max-w-32 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                            {formatDate((doc as any).data_lancamento || (doc as any).data_emissao)}
                          </td> 
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${statusCfg.className}`}
                            >
                              {statusCfg.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* PIE CHARTS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <DashboardPieCard
            title="Aprovação de Documentos"
            data={aprovacaoDocsData}
          />

          <DashboardPieCard
            title="Status das Prestações"
            data={statusPrestacoesData}
          />
        </div>

        {/* AÇÕES RÁPIDAS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Ações Rápidas
              </h2>
              <TrendingUp className="w-5 h-5 text-primary-600" />
            </div>
            <div className="space-y-3">
              <Link
                to="/prestacoes"
                className="block w-full text-left px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <p className="font-medium text-gray-900 dark:text-white">
                  Ver Prestações de Contas
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Gerencie suas prestações de contas
                </p>
              </Link>
              {isAdmin() && (
                <Link
                  to="/prestacoes/nova"
                  className="block w-full text-left px-4 py-3 rounded-lg bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
                >
                  <p className="font-medium text-primary-900 dark:text-primary-100">
                    Nova Prestação de Contas
                  </p>
                  <p className="text-sm text-primary-700 dark:text-primary-300">
                    Criar uma nova prestação de contas
                  </p>
                </Link>
              )}
              {false && isAdmin() && (
                <Link
                  to="/usuarios"
                  className="block w-full text-left px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <p className="font-medium text-gray-900 dark:text-white">
                    Gerenciar Usuários
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Administrar usuários clientes
                  </p>
                </Link>
              )}
            </div>
          </div>

          {/* INFORMACOES */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Informações
            </h2>
            <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
              <p>
                Sistema desenvolvido para gerenciar prestações de contas de forma transparente e eficiente.
              </p>
              <p>
                Acompanhe documentos fiscais, valide despesas e mantenha o controle completo de suas contas.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
